import { useForm, revalidateLogic } from "@tanstack/react-form"
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import { createFileRoute } from "@tanstack/react-router"
import { useMemo, useState } from "react"
import { z } from "zod"

import { DateTimeField } from "@/components/custom/date-time-field"
import { EventDetailActions } from "@/components/guild-events-admin/event-detail-actions"
import { EventDetailMetadata } from "@/components/guild-events-admin/event-detail-metadata"
import { SignupColumnsCard } from "@/components/guild-events-admin/event-detail-signups"
import {
  EventFormFields,
  projectEventInput,
  validateEventInput,
  type EventFormState,
} from "@/components/guild-events-admin/event-form-fields"
import { ManualRsvpOverride } from "@/components/guild-events-admin/manual-rsvp-override"
import { PageIntroSplitHeader } from "@/components/layout/page-intro-split-header"
import { EventStatusBadge } from "@/components/shared/event-status-badge"
import { MetricTile } from "@/components/shared/metric-tile"
import { RefreshButton } from "@/components/shared/refresh-button"
import { SectionKicker } from "@/components/shared/section-kicker"
import { StatusAlert } from "@/components/shared/status-alert"
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { Card, CardContent } from "@/components/ui/card"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"
import { DetailPageSkeleton } from "@/components/ui/page-skeletons"
import { toast } from "@/components/ui/toast"
import { getSyncStatusLabel } from "@/core/guild-events/labels"
import { formatDateTimeOffsetHint } from "@/lib/guild-date-format"
import { guildEventsApi, type GuildEventRecord } from "@/lib/guild-events-api"
import { guildEventsKeys } from "@/lib/query-keys"
import { createGuildEventsAdminDetailStore } from "@/lib/stores/guild-events-admin-detail-store"

export const Route = createFileRoute("/_authed/_admin/guild-events/$eventId")({
  validateSearch: z.object({ memberId: z.string().trim().optional() }),
  beforeLoad: () => ({
    guildEventsDetailStore: createGuildEventsAdminDetailStore(),
  }),
  component: GuildEventDetailRoute,
})

function isoToLocalInput(iso: string): string {
  const d = new Date(iso)
  const pad = (n: number) => String(n).padStart(2, "0")
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`
}

function EditEventForm({
  initialValues,
  pending,
  onCancel,
  onSubmit,
  onSubmitInvalid,
  validationSummary,
}: {
  initialValues: EventFormState
  pending: boolean
  onCancel: () => void
  onSubmit: (value: EventFormState) => void
  onSubmitInvalid: () => void
  validationSummary: string | null
}) {
  const form = useForm({
    defaultValues: initialValues,
    validationLogic: revalidateLogic({
      mode: "submit",
      modeAfterSubmission: "change",
    }),
    validators: { onDynamic: ({ value }) => validateEventInput(value) },
    onSubmitInvalid,
    onSubmit: ({ value }) => onSubmit(value),
  })

  return (
    <Card>
      <CardContent className="pt-5">
        <div className="mb-3 text-sm font-semibold">Edit Event</div>
        <form
          className="grid gap-3 md:grid-cols-2"
          onSubmit={(event) => {
            event.preventDefault()
            void form.handleSubmit()
          }}
          noValidate
        >
          <form.Subscribe selector={(state) => state.values}>
            {(values) => (
              <EventFormFields
                bindings={{
                  values,
                  eventKind: (children) => (
                    <form.Field name="eventKind">{children}</form.Field>
                  ),
                  title: (children) => (
                    <form.Field name="title">{children}</form.Field>
                  ),
                  description: (children) => (
                    <form.Field name="description">{children}</form.Field>
                  ),
                  eventRoles: (children) => (
                    <form.Field name="eventRoles">{children}</form.Field>
                  ),
                  startAt: (children) => (
                    <form.Field name="startAt">{children}</form.Field>
                  ),
                  signupCloseAt: (children) => (
                    <form.Field name="signupCloseAt">{children}</form.Field>
                  ),
                  rsvpEnabled: (children) => (
                    <form.Field name="rsvpEnabled">{children}</form.Field>
                  ),
                  setEventRoles: (eventRoles) =>
                    form.setFieldValue("eventRoles", eventRoles),
                  setSignupCloseAt: (signupCloseAt) =>
                    form.setFieldValue("signupCloseAt", signupCloseAt ?? ""),
                }}
                eventKindReadOnly
              />
            )}
          </form.Subscribe>
          {validationSummary ? (
            <div
              role="alert"
              className="text-sm text-destructive md:col-span-2"
            >
              {validationSummary}
            </div>
          ) : null}
          <div className="flex gap-2 md:col-span-2">
            <Button type="submit" disabled={pending}>
              {pending ? "Saving..." : "Save Changes"}
            </Button>
            <Button
              type="button"
              variant="outline"
              onClick={onCancel}
              disabled={pending}
            >
              Cancel
            </Button>
          </div>
        </form>
      </CardContent>
    </Card>
  )
}

function GuildEventDetailRoute() {
  const { eventId } = Route.useParams()
  const navigate = Route.useNavigate()
  const queryClient = useQueryClient()

  const detailQuery = useQuery({
    queryKey: guildEventsKeys.adminDetail(eventId),
    queryFn: () => guildEventsApi.getAdminEventDetail(eventId),
  })
  const schedulesQuery = useQuery({
    queryKey: guildEventsKeys.schedules(),
    queryFn: guildEventsApi.listSchedules,
  })
  const currentEvent = detailQuery.data?.event
  const signups = detailQuery.data?.signups ?? []
  const scheduleTitles = useMemo(
    () =>
      new Map(
        schedulesQuery.data?.map((schedule) => [schedule.id, schedule.title]),
      ),
    [schedulesQuery.data],
  )
  const scheduleTitle = currentEvent?.scheduleId
    ? scheduleTitles.get(currentEvent.scheduleId)
    : undefined
  const [isCancelOpen, setIsCancelOpen] = useState(false)
  const [isDeleteOpen, setIsDeleteOpen] = useState(false)
  const [isDuplicateOpen, setIsDuplicateOpen] = useState(false)
  const [dupStartAt, setDupStartAt] = useState("")
  const [dupCloseAt, setDupCloseAt] = useState("")
  const [isEditing, setIsEditing] = useState(false)
  const [editForm, setEditForm] = useState<EventFormState | null>(null)
  const [editError, setEditError] = useState<string | null>(null)

  const cancelMutation = useMutation({
    mutationFn: () => guildEventsApi.cancelEvent(eventId),
    onSuccess: async () => {
      setIsCancelOpen(false)
      toast.success("Event cancelled")
      await queryClient.invalidateQueries({
        queryKey: guildEventsKeys.all,
      })
    },
    onError: (error: Error) => toast.error(error.message),
  })

  const deleteMutation = useMutation({
    mutationFn: () => guildEventsApi.deleteEvent(eventId),
    onSuccess: async () => {
      queryClient.setQueriesData<{
        items: GuildEventRecord[]
        total: number
      }>({ queryKey: [...guildEventsKeys.all, "admin", "list"] }, (current) => {
        if (!current) return current
        const items = current.items.filter((event) => event.id !== eventId)
        return items.length === current.items.length
          ? current
          : { items, total: current.total - 1 }
      })
      queryClient.removeQueries({
        queryKey: guildEventsKeys.adminDetail(eventId),
        exact: true,
      })
      setIsDeleteOpen(false)
      toast.success("Event deleted")
      await navigate({ to: "/guild-events" })
      void queryClient.invalidateQueries({
        queryKey: guildEventsKeys.all,
      })
    },
    onError: (error: Error) => toast.error(error.message),
  })

  const repostMutation = useMutation({
    mutationFn: () => guildEventsApi.repostAnnouncement(eventId),
    onSuccess: async () => {
      toast.success("Announcement reposted")
      await queryClient.invalidateQueries({
        queryKey: guildEventsKeys.all,
      })
    },
    onError: (error: Error) => toast.error(error.message),
  })

  const syncMutation = useMutation({
    mutationFn: () => guildEventsApi.syncAnnouncementFromDb(eventId),
    onSuccess: async () => {
      toast.success("Synced from database")
      await queryClient.invalidateQueries({
        queryKey: guildEventsKeys.all,
      })
    },
    onError: (error: Error) => toast.error(error.message),
  })
  const duplicateMutation = useMutation({
    mutationFn: (input: Parameters<typeof guildEventsApi.duplicateEvent>[1]) =>
      guildEventsApi.duplicateEvent(eventId, input),
    onSuccess: async (cloned) => {
      await queryClient.invalidateQueries({
        queryKey: guildEventsKeys.all,
      })
      setIsDuplicateOpen(false)
      setDupStartAt("")
      setDupCloseAt("")
      toast.success("Event duplicated")
      await navigate({
        to: "/guild-events/$eventId",
        params: { eventId: cloned.id },
      })
    },
    onError: (error: Error) => toast.error(error.message),
  })

  const editMutation = useMutation({
    mutationFn: (input: Parameters<typeof guildEventsApi.updateEvent>[1]) =>
      guildEventsApi.updateEvent(eventId, input),
    onSuccess: async () => {
      setIsEditing(false)
      setEditForm(null)
      setEditError(null)
      toast.success("Event updated")
      await queryClient.invalidateQueries({
        queryKey: guildEventsKeys.all,
      })
    },
    onError: (error: Error) => toast.error(error.message),
  })

  if (detailQuery.isLoading) {
    return <DetailPageSkeleton />
  }

  if (detailQuery.isError) {
    return (
      <div className="grid gap-4 md:gap-5">
        <StatusAlert tone="danger">
          <div className="flex items-center justify-between gap-3">
            <span>Failed to load event details.</span>
            <button
              type="button"
              className="shrink-0 text-sm font-medium underline underline-offset-2 hover:no-underline"
              onClick={() => void detailQuery.refetch()}
            >
              Retry
            </button>
          </div>
        </StatusAlert>
        <Button
          type="button"
          variant="outline"
          onClick={() => void navigate({ to: "/guild-events" })}
        >
          Back to Events
        </Button>
      </div>
    )
  }

  return (
    <div className="grid gap-4 md:gap-5">
      <Card className="page-intro border-primary/10">
        <PageIntroSplitHeader
          kicker={<SectionKicker>Guild Events</SectionKicker>}
          title={currentEvent?.title ?? "Guild Event"}
          description={
            currentEvent?.description ? currentEvent.description : ""
          }
          actions={
            <div className="flex gap-2">
              <RefreshButton
                isFetching={detailQuery.isFetching || schedulesQuery.isFetching}
                onClick={() => {
                  void detailQuery.refetch()
                  void schedulesQuery.refetch()
                }}
                variant="outline"
              />
              <Button
                type="button"
                variant="outline"
                onClick={() => void navigate({ to: "/guild-events" })}
              >
                Back
              </Button>
            </div>
          }
          metrics={
            <>
              <MetricTile label="Status">
                {currentEvent ? (
                  <EventStatusBadge status={currentEvent.status} />
                ) : (
                  "-"
                )}
              </MetricTile>
              <MetricTile label="Sync">
                {currentEvent ? (
                  currentEvent.syncStatus === "degraded" ? (
                    <Badge variant="destructive">
                      {getSyncStatusLabel(currentEvent.syncStatus)}
                    </Badge>
                  ) : (
                    <Badge variant="secondary">
                      {getSyncStatusLabel(currentEvent.syncStatus)}
                    </Badge>
                  )
                ) : (
                  "-"
                )}
              </MetricTile>
              {currentEvent?.rsvpEnabled ? (
                <MetricTile label="Signups" value={String(signups.length)} />
              ) : null}
            </>
          }
        />
      </Card>

      {isEditing && editForm && currentEvent ? (
        <EditEventForm
          initialValues={editForm}
          pending={editMutation.isPending}
          validationSummary={editError}
          onSubmit={(value) => editMutation.mutate(projectEventInput(value))}
          onSubmitInvalid={() => setEditError("Fix the highlighted errors.")}
          onCancel={() => {
            setEditForm(null)
            setEditError(null)
            setIsEditing(false)
          }}
        />
      ) : null}

      <EventDetailMetadata event={currentEvent} scheduleTitle={scheduleTitle} />
      <EventDetailActions
        event={currentEvent}
        onEdit={() => {
          if (!currentEvent) return
          setEditForm(
            currentEvent.eventKind === "raid"
              ? {
                  eventKind: "raid",
                  title: currentEvent.title,
                  description: currentEvent.description,
                  eventRoles: currentEvent.eventRoles,
                  startAt: isoToLocalInput(currentEvent.startAt),
                  signupCloseAt: isoToLocalInput(currentEvent.signupCloseAt),
                  rsvpEnabled: currentEvent.rsvpEnabled,
                }
              : {
                  eventKind: "attendance",
                  title: currentEvent.title,
                  description: currentEvent.description,
                  eventRoles: [],
                  startAt: isoToLocalInput(currentEvent.startAt),
                  signupCloseAt: null,
                  rsvpEnabled: currentEvent.rsvpEnabled,
                },
          )
          setEditError(null)
          setIsEditing(true)
        }}
        editPending={editMutation.isPending}
        onCancel={() => setIsCancelOpen(true)}
        onRepost={() => repostMutation.mutate()}
        onSync={() => syncMutation.mutate()}
        cancelPending={cancelMutation.isPending}
        repostPending={repostMutation.isPending}
        syncPending={syncMutation.isPending}
        onDuplicate={() => setIsDuplicateOpen(true)}
        duplicatePending={duplicateMutation.isPending}
        onDelete={() => setIsDeleteOpen(true)}
        deletePending={deleteMutation.isPending}
      />

      {currentEvent?.rsvpEnabled ? (
        <>
          <ManualRsvpOverride
            eventId={eventId}
            signups={signups}
            eventRoles={currentEvent.eventRoles}
          />
          <SignupColumnsCard signups={signups} />
        </>
      ) : null}
      <AlertDialog open={isCancelOpen} onOpenChange={setIsCancelOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Cancel Event</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to cancel this event? This will cancel all
              scheduled jobs and notify members. This cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogAction
              variant="destructive"
              onClick={() => cancelMutation.mutate()}
              disabled={cancelMutation.isPending}
            >
              {cancelMutation.isPending ? "Cancelling..." : "Yes, Cancel Event"}
            </AlertDialogAction>
            <AlertDialogCancel>Keep Event</AlertDialogCancel>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={isDeleteOpen} onOpenChange={setIsDeleteOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Event</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to permanently delete this event? This
              cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogAction
              variant="destructive"
              onClick={() => deleteMutation.mutate()}
              disabled={deleteMutation.isPending}
            >
              {deleteMutation.isPending ? "Deleting..." : "Yes, Delete Event"}
            </AlertDialogAction>
            <AlertDialogCancel disabled={deleteMutation.isPending}>
              Keep Event
            </AlertDialogCancel>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <Dialog open={isDuplicateOpen} onOpenChange={setIsDuplicateOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Duplicate Event</DialogTitle>
            <DialogDescription>
              Create a copy of this event with a new start time
              {currentEvent?.eventKind === "raid"
                ? " and signup-close time"
                : ""}
              .
            </DialogDescription>
          </DialogHeader>
          <div className="grid gap-3 py-2">
            <div className="grid gap-1 text-sm font-medium">
              <span>Start At</span>
              <DateTimeField
                id="duplicate-start-at"
                value={dupStartAt}
                onValueChange={(value) => setDupStartAt(value ?? "")}
                dateAriaLabel="Duplicate start date"
                timeAriaLabel="Duplicate start time"
                minuteStep={1}
              />
              {dupStartAt ? (
                <p className="text-xs text-muted-foreground">
                  {formatDateTimeOffsetHint(dupStartAt)}
                </p>
              ) : null}
            </div>
            {currentEvent?.eventKind === "raid" ? (
              <div className="grid gap-1 text-sm font-medium">
                <span>Signup Close At</span>
                <DateTimeField
                  id="duplicate-signup-close-at"
                  value={dupCloseAt}
                  onValueChange={(value) => setDupCloseAt(value ?? "")}
                  dateAriaLabel="Duplicate signup close date"
                  timeAriaLabel="Duplicate signup close time"
                  minuteStep={1}
                />
                {dupCloseAt ? (
                  <p className="text-xs text-muted-foreground">
                    {formatDateTimeOffsetHint(dupCloseAt)}
                  </p>
                ) : null}
              </div>
            ) : null}
          </div>
          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => setIsDuplicateOpen(false)}
              disabled={duplicateMutation.isPending}
            >
              Cancel
            </Button>
            <Button
              type="button"
              disabled={
                duplicateMutation.isPending ||
                !dupStartAt ||
                (currentEvent?.eventKind === "raid" &&
                  (!dupCloseAt ||
                    new Date(dupCloseAt) >= new Date(dupStartAt) ||
                    new Date(dupCloseAt) < new Date()))
              }
              onClick={() => {
                if (!currentEvent || !dupStartAt) {
                  toast.error("Please select a start time.")
                  return
                }
                if (currentEvent.eventKind === "raid") {
                  if (!dupCloseAt) {
                    toast.error("Please select a signup-close time.")
                    return
                  }
                  if (new Date(dupCloseAt) >= new Date(dupStartAt)) {
                    toast.error(
                      "Signup close must be before the event start time.",
                    )
                    return
                  }
                  if (new Date(dupCloseAt) < new Date()) {
                    toast.error("Signup close time must be in the future.")
                    return
                  }
                  duplicateMutation.mutate({
                    eventKind: "raid",
                    startAt: new Date(dupStartAt).toISOString(),
                    signupCloseAt: new Date(dupCloseAt).toISOString(),
                  })
                  return
                }
                duplicateMutation.mutate({
                  eventKind: "attendance",
                  startAt: new Date(dupStartAt).toISOString(),
                })
              }}
            >
              {duplicateMutation.isPending
                ? "Duplicating..."
                : "Create Duplicate"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  )
}
